import React, { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { toast } from 'sonner';
import {
    CheckCircle2,
    ArrowRight,
    Undo2,
    RefreshCw,
    FileText,
    PenLine,
    Landmark,
    Lock,
} from 'lucide-react';
import * as YearEndService from '../../../../services/YearEndService';
import { formatSek } from '../constants';

const ROLE_LABELS = {
    ledamot: 'Styrelseledamot',
    ordforande: 'Styrelseordförande',
    vd: 'VD',
    suppleant: 'Suppleant',
};

/** Parse a Swedish-typed amount ("12 500,50") into a number. */
function parseAmount(raw) {
    if (raw == null) return NaN;
    const cleaned = String(raw).replace(/[\s ]/g, '').replace(',', '.');
    if (cleaned === '') return NaN;
    return Number(cleaned);
}

function parseOptionalAmount(raw) {
    if (String(raw ?? '').trim() === '') return 0;
    return parseAmount(raw);
}

function round2(n) { return Math.round(n * 100) / 100; }

/**
 * Step 6 — Underskrifter & årsstämma, over the annual_reports contract
 * row. Two sub-cards: (1) the print-sign flow — print the ÅR, collect
 * every board member's (+ ev. VD's) signature (ÅRL 2:7) and confirm here
 * (POST signatures; undo via inline confirm, 409 once the stämma is
 * recorded); (2) the ordinarie årsstämma minutes (ABL 7:10–11) — roles,
 * fastställelse (mandatory), ansvarsfrihet and the stämma's
 * resultatdisposition (prefilled from the styrelse proposal but editable:
 * stämman beslutar). Warnings from the backend (e.g. >6 months after FY
 * end) are shown but never block. Recorded state shows the decisions +
 * protokoll PDF + undo. Backend Swedish messages are shown verbatim.
 */
const SignaturesStammaStep = ({
    companyId,
    processId,
    completed,
    onComplete,
    onUndo,
    busy,
    gateError,
}) => {
    const [report, setReport] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    // Underskrifter
    const [signDate, setSignDate] = useState(moment().format('YYYY-MM-DD'));
    const [signLocation, setSignLocation] = useState('');
    const [signing, setSigning] = useState(false);
    const [signError, setSignError] = useState(null);
    const [confirmUndoSign, setConfirmUndoSign] = useState(false);
    const [undoingSign, setUndoingSign] = useState(false);

    // Stämman
    const [stammaDate, setStammaDate] = useState(moment().format('YYYY-MM-DD'));
    const [ordforande, setOrdforande] = useState('');
    const [protokollforare, setProtokollforare] = useState('');
    const [justerare, setJusterare] = useState('');
    const [faststallde, setFaststallde] = useState(false);
    const [ansvarsfrihet, setAnsvarsfrihet] = useState(true);
    // Reviderat bolag (ÅRL 8:3/ABL 7:11): revisionsberättelsens datum krävs
    // av servern när fb_sections.revisor.harRevisor är satt.
    const [revisionsberattelseDatum, setRevisionsberattelseDatum] = useState('');
    const [utdelningInput, setUtdelningInput] = useState('');
    const [yttrande, setYttrande] = useState('');
    const dispSeeded = useRef(false);
    const [registering, setRegistering] = useState(false);
    const [stammaError, setStammaError] = useState(null);
    const [stammaWarnings, setStammaWarnings] = useState([]);
    const [confirmUndoStamma, setConfirmUndoStamma] = useState(false);
    const [undoingStamma, setUndoingStamma] = useState(false);

    const [openingPdf, setOpeningPdf] = useState(null); // 'ar' | 'protokoll'

    // ---- Load -------------------------------------------------------------

    const applyReport = useCallback((r) => {
        setReport(r);
        if (!r) return;
        // Ort förifylls i andra hand från styrelsens säte i FB (ABL 7:15 —
        // stämman hålls på säteorten om inte bolagsordningen anger annat);
        // sätet är i sin tur ev. TIC-förifyllt men användarbekräftat i ÅR-steget.
        const ortDefault = r.signature_location || r.fb_sections?.sate || '';
        if (ortDefault) setSignLocation((prev) => prev || ortDefault);
        // C2 (2026-08-11): yttrandet författas i ÅR-steget (fb_sections) —
        // förifyll det här så stämman "framlägger" samma text.
        if (r.fb_sections?.yttrande_18_4) {
            setYttrande((prev) => prev || r.fb_sections.yttrande_18_4);
        }
        const first = Array.isArray(r.board_members) ? r.board_members[0] : null;
        if (first?.name) {
            setOrdforande((prev) => prev || first.name);
            setProtokollforare((prev) => prev || first.name);
        }
        if (!dispSeeded.current) {
            dispSeeded.current = true;
            const disp = r.stamma_decisions?.resultatdisposition || r.resultatdisposition;
            if (disp && Number(disp.utdelning) > 0) setUtdelningInput(String(disp.utdelning));
        }
    }, []);

    const fetchReport = useCallback(async ({ silent = false } = {}) => {
        if (!processId) return;
        if (!silent) setRefreshing(true);
        setLoadError(null);
        try {
            const resp = await YearEndService.getAnnualReport(companyId, processId);
            applyReport(resp?.data?.report || null);
        } catch (err) {
            setLoadError(err.response?.data?.message
                || 'Kunde inte hämta uppgifterna. Försök igen senare.');
        } finally {
            setRefreshing(false);
        }
    }, [companyId, processId, applyReport]);

    useEffect(() => { fetchReport({ silent: true }); }, [fetchReport]);

    const status = report?.status || 'draft';
    const isDraft = status === 'draft';
    const isSigned = ['signed', 'stamma_held', 'completed'].includes(status);
    const stammaRecorded = ['stamma_held', 'completed'].includes(status);
    const boardMembers = Array.isArray(report?.board_members) ? report.board_members : [];

    // Disposition shown/edited in the stämma form. Basis = styrelsens förslag.
    const proposal = report?.resultatdisposition || null;
    const till = Number(proposal?.tillDisposition) || 0;
    const utdelningValue = parseOptionalAmount(utdelningInput);
    const utdelningValid = Number.isFinite(utdelningValue) && utdelningValue >= 0;
    const balanseras = utdelningValid ? round2(till - utdelningValue) : null;
    const dispositionOk = utdelningValid && balanseras != null && balanseras >= -0.005;

    // ---- Actions ----------------------------------------------------------

    // Nedladdning med riktigt filnamn — det här är dokumentet som skrivs ut
    // för underskrift (och senare lämnas in), inte en förhandsgranskning.
    const handleOpenArPdf = async () => {
        setOpeningPdf('ar');
        try {
            const name = await YearEndService.downloadAnnualReportPdf(companyId, processId);
            if (name) toast.success(`${name} laddades ner.`);
        } catch (err) {
            toast.error(err.response?.data?.message
                || 'Kunde inte ladda ner årsredovisningen.');
        } finally {
            setOpeningPdf(null);
        }
    };

    const handleOpenProtokollPdf = async () => {
        setOpeningPdf('protokoll');
        try {
            await YearEndService.openStammaProtokollPdf(companyId, processId);
        } catch (err) {
            toast.error(err.response?.data?.message
                || 'Kunde inte öppna protokollet.');
        } finally {
            setOpeningPdf(null);
        }
    };

    const handleRecordSignatures = async () => {
        setSigning(true);
        setSignError(null);
        try {
            const resp = await YearEndService.recordSignatures(companyId, processId, {
                signatureDate: signDate,
                signatureLocation: signLocation.trim() || undefined,
            });
            toast.success('Underskrifterna är bekräftade.');
            applyReport(resp?.data || null);
        } catch (err) {
            setSignError(err.response?.data?.message
                || 'Kunde inte bekräfta underskrifterna.');
        } finally {
            setSigning(false);
        }
    };

    const handleUndoSignatures = async () => {
        setUndoingSign(true);
        try {
            const resp = await YearEndService.undoSignatures(companyId, processId);
            toast.success('Underskrifterna är ångrade.');
            setConfirmUndoSign(false);
            applyReport(resp?.data || null);
        } catch (err) {
            toast.error(err.response?.data?.message
                || 'Kunde inte ångra underskrifterna.');
        } finally {
            setUndoingSign(false);
        }
    };

    const handleRecordStamma = async () => {
        setRegistering(true);
        setStammaError(null);
        try {
            const resp = await YearEndService.recordStamma(companyId, processId, {
                stammaDate,
                decisions: {
                    faststallde: true,
                    ansvarsfrihet,
                    ordforande: ordforande.trim(),
                    protokollforare: protokollforare.trim() || ordforande.trim(),
                    justerare: justerare.trim()
                        ? justerare.split(',').map((s) => s.trim()).filter(Boolean)
                        : [],
                    resultatdisposition: {
                        tillDisposition: round2(till),
                        utdelning: round2(utdelningValue),
                        balanserasINyRakning: Math.max(0, round2(till - utdelningValue)),
                    },
                    ...(utdelningValue > 0 && yttrande.trim()
                        ? { yttrande18_4: yttrande.trim() } : {}),
                    ...(revisionsberattelseDatum
                        ? { revisionsberattelseDatum } : {}),
                },
            });
            toast.success('Årsstämman är registrerad.');
            setStammaWarnings(Array.isArray(resp?.data?.warnings) ? resp.data.warnings : []);
            applyReport(resp?.data?.report || null);
        } catch (err) {
            setStammaError(err.response?.data?.message
                || 'Kunde inte registrera stämman.');
        } finally {
            setRegistering(false);
        }
    };

    const handleUndoStamma = async () => {
        setUndoingStamma(true);
        try {
            const resp = await YearEndService.undoStamma(companyId, processId);
            toast.success('Registreringen av stämman är ångrad.');
            setConfirmUndoStamma(false);
            setStammaWarnings([]);
            applyReport(resp?.data || null);
        } catch (err) {
            toast.error(err.response?.data?.message
                || 'Kunde inte ångra stämman.');
        } finally {
            setUndoingStamma(false);
        }
    };

    // ---- Renderers --------------------------------------------------------

    const renderSignaturesCard = () => (
        <div className="ye-sub-card">
            <div className="ye-sub-card-head">
                <span className="ye-sub-card-icon"><PenLine size={18} /></span>
                <div>
                    <div className="ye-sub-card-title">Underskrifter</div>
                    <div className="ye-sub-card-sub">
                        Skriv ut årsredovisningen, låt hela styrelsen och eventuell VD
                        skriva under — ÅRL kräver att alla skriver under — och bekräfta
                        sedan här.
                    </div>
                </div>
                {isSigned && (
                    <span className="ye-badge ye-badge-success" style={{ marginLeft: 'auto' }}>
                        <CheckCircle2 size={13} />
                        Underskriven
                    </span>
                )}
            </div>

            {isDraft ? (
                <div className="ye-well">
                    Färdigställ årsredovisningen först (steget Årsredovisning) — sedan
                    kan underskrifterna samlas in.
                </div>
            ) : isSigned ? (
                <>
                    <div className="ye-tax-rows" style={{ marginTop: '0.75rem' }}>
                        {boardMembers.map((m, i) => (
                            <div className="ye-tax-row" key={i}>
                                <span>
                                    {m.name}
                                    <span className="text-muted"> — {ROLE_LABELS[m.role] || m.role}</span>
                                </span>
                                <span className="ye-num">{m.signedAt || report?.signature_date || ''}</span>
                            </div>
                        ))}
                    </div>
                    {report?.signature_location && (
                        <div className="ye-field-help" style={{ marginTop: '0.4rem' }}>
                            Ort: {report.signature_location}
                        </div>
                    )}
                    {!stammaRecorded && (
                        <div style={{ marginTop: '0.75rem' }}>
                            <button
                                className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
                                onClick={() => setConfirmUndoSign(true)}
                                disabled={undoingSign || confirmUndoSign}
                            >
                                <Undo2 size={13} />
                                Ångra underskrifterna
                            </button>
                            {confirmUndoSign && (
                                <div className="ye-inline-confirm">
                                    <span>
                                        Bekräftelsen tas bort och årsredovisningen kan redigeras
                                        igen. Vill du fortsätta?
                                    </span>
                                    <span className="ye-inline-confirm-actions">
                                        <button
                                            className="btn btn-danger btn-sm"
                                            onClick={handleUndoSignatures}
                                            disabled={undoingSign}
                                        >
                                            {undoingSign ? 'Ångrar…' : 'Ja, ångra underskrifterna'}
                                        </button>
                                        <button
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => setConfirmUndoSign(false)}
                                            disabled={undoingSign}
                                        >
                                            Avbryt
                                        </button>
                                    </span>
                                </div>
                            )}
                        </div>
                    )}
                </>
            ) : (
                <>
                    <div className="ye-proposal-actions" style={{ marginTop: '0.75rem' }}>
                        <button
                            className="btn btn-outline-primary d-inline-flex align-items-center gap-2"
                            onClick={handleOpenArPdf}
                            disabled={openingPdf === 'ar'}
                        >
                            <FileText size={16} />
                            {openingPdf === 'ar' ? 'Öppnar…' : 'Årsredovisning (PDF)'}
                        </button>
                    </div>
                    <div className="ye-form-grid" style={{ marginTop: '0.75rem' }}>
                        <div className="ye-field">
                            <label htmlFor="ye-sign-date">Datum för underskrift</label>
                            <input
                                id="ye-sign-date"
                                type="date"
                                value={signDate}
                                onChange={(e) => setSignDate(e.target.value)}
                            />
                        </div>
                        <div className="ye-field">
                            <label htmlFor="ye-sign-location">Ort</label>
                            <input
                                id="ye-sign-location"
                                type="text"
                                placeholder="t.ex. Göteborg"
                                value={signLocation}
                                onChange={(e) => setSignLocation(e.target.value)}
                            />
                        </div>
                    </div>
                    {signError && (
                        <div className="alert alert-danger mt-3 mb-0" style={{ fontSize: '0.875rem' }}>
                            {signError}
                        </div>
                    )}
                    <div className="ye-proposal-actions">
                        <button
                            className="btn btn-primary d-inline-flex align-items-center gap-2"
                            onClick={handleRecordSignatures}
                            disabled={signing || !signDate}
                        >
                            <PenLine size={16} />
                            {signing ? 'Bekräftar…' : 'Bekräfta att alla har skrivit under'}
                        </button>
                        <span className="ye-footer-hint">
                            Bekräfta först när samtliga underskrifter finns på papperet.
                        </span>
                    </div>
                </>
            )}
        </div>
    );

    const renderStammaSummary = () => {
        const d = report?.stamma_decisions || {};
        const disp = d.resultatdisposition || proposal || {};
        return (
            <>
                <div className="ye-tax-rows" style={{ marginTop: '0.75rem' }}>
                    <div className="ye-tax-row">
                        <span>Stämmodatum</span>
                        <span className="ye-num">{report?.stamma_date || ''}</span>
                    </div>
                    <div className="ye-tax-row">
                        <span>Ordförande</span>
                        <span>{d.ordforande || '—'}</span>
                    </div>
                    <div className="ye-tax-row">
                        <span>Protokollförare</span>
                        <span>{d.protokollforare || d.ordforande || '—'}</span>
                    </div>
                    <div className="ye-tax-row">
                        <span>Justerare</span>
                        <span>{(d.justerare || []).join(', ') || 'Ordföranden'}</span>
                    </div>
                    <div className="ye-tax-row">
                        <span>Fastställde resultat- och balansräkningen</span>
                        <span>Ja</span>
                    </div>
                    <div className="ye-tax-row">
                        <span>Ansvarsfrihet beviljades</span>
                        <span>{d.ansvarsfrihet === false ? 'Nej' : 'Ja'}</span>
                    </div>
                    <div className="ye-tax-row ye-tax-row-group">
                        <span>Utdelning enligt stämmobeslut</span>
                        <span className="ye-num">{formatSek(disp.utdelning || 0)}</span>
                    </div>
                    <div className="ye-tax-row">
                        <span>Balanseras i ny räkning</span>
                        <span className="ye-num">{formatSek(disp.balanserasINyRakning ?? disp.tillDisposition)}</span>
                    </div>
                </div>

                <div className="ye-proposal-actions">
                    <button
                        className="btn btn-outline-primary d-inline-flex align-items-center gap-2"
                        onClick={handleOpenProtokollPdf}
                        disabled={openingPdf === 'protokoll'}
                    >
                        <FileText size={16} />
                        {openingPdf === 'protokoll' ? 'Öppnar…' : 'Protokoll (PDF)'}
                    </button>
                    {status === 'stamma_held' && (
                        <button
                            className="btn btn-outline-secondary d-inline-flex align-items-center gap-2"
                            onClick={() => setConfirmUndoStamma(true)}
                            disabled={undoingStamma || confirmUndoStamma}
                        >
                            <Undo2 size={14} />
                            Ångra registreringen
                        </button>
                    )}
                </div>
                {confirmUndoStamma && (
                    <div className="ye-inline-confirm">
                        <span>
                            Registreringen av stämman tas bort — protokollet och besluten
                            försvinner ur systemet (inget i bokföringen påverkas). Vill du
                            fortsätta?
                        </span>
                        <span className="ye-inline-confirm-actions">
                            <button
                                className="btn btn-danger btn-sm"
                                onClick={handleUndoStamma}
                                disabled={undoingStamma}
                            >
                                {undoingStamma ? 'Ångrar…' : 'Ja, ångra registreringen'}
                            </button>
                            <button
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => setConfirmUndoStamma(false)}
                                disabled={undoingStamma}
                            >
                                Avbryt
                            </button>
                        </span>
                    </div>
                )}
            </>
        );
    };

    const renderStammaForm = () => (
        <>
            <div className="ye-form-grid" style={{ marginTop: '0.75rem' }}>
                <div className="ye-field">
                    <label htmlFor="ye-stamma-date">Stämmodatum</label>
                    <input
                        id="ye-stamma-date"
                        type="date"
                        value={stammaDate}
                        onChange={(e) => setStammaDate(e.target.value)}
                    />
                    <div className="ye-field-help">
                        Ordinarie stämma ska hållas inom sex månader efter räkenskapsårets
                        slut.
                    </div>
                </div>
                <div className="ye-field">
                    <label htmlFor="ye-stamma-ordf">Ordförande</label>
                    <input
                        id="ye-stamma-ordf"
                        type="text"
                        value={ordforande}
                        onChange={(e) => setOrdforande(e.target.value)}
                    />
                </div>
                <div className="ye-field">
                    <label htmlFor="ye-stamma-prot">Protokollförare</label>
                    <input
                        id="ye-stamma-prot"
                        type="text"
                        value={protokollforare}
                        onChange={(e) => setProtokollforare(e.target.value)}
                    />
                </div>
                <div className="ye-field">
                    <label htmlFor="ye-stamma-just">Justerare</label>
                    <input
                        id="ye-stamma-just"
                        type="text"
                        placeholder="Namn — flera skiljs med komma"
                        value={justerare}
                        onChange={(e) => setJusterare(e.target.value)}
                    />
                </div>
                {/* Reviderat bolag: revisionsberättelsen läggs fram på stämman
                    (ABL 7:11) och lämnas in med ÅR:t (ÅRL 8:3) — servern
                    kräver datumet när bolaget har revisor. */}
                {report?.fb_sections?.revisor?.harRevisor === true && (
                    <div className="ye-field">
                        <label htmlFor="ye-stamma-rb">Revisionsberättelsens datum</label>
                        <input
                            id="ye-stamma-rb"
                            type="date"
                            value={revisionsberattelseDatum}
                            max={stammaDate || undefined}
                            onChange={(e) => setRevisionsberattelseDatum(e.target.value)}
                        />
                        <div className="ye-field-help">
                            Bolaget har revisor — revisionsberättelsen ska vara upprättad
                            och läggas fram på stämman (senast tre veckor före, ABL 9:28).
                        </div>
                    </div>
                )}
            </div>
            <div className="ye-field-help" style={{ marginTop: '0.4rem', maxWidth: '68ch' }}>
                I ett enmansbolag kan samma person ha alla roller.
            </div>
            {/* C8 (2026-08-11): justerare-hint — ABL 7:48 kräver att protokollet
                justeras av minst en person UTÖVER ordföranden när denne fört
                protokollet; undantag när ordföranden/protokollföraren företräder
                samtliga aktier (enmansbolag). */}
            {!justerare.trim()
                && (protokollforare.trim() === '' || protokollforare.trim() === ordforande.trim()) && (
                <div className="alert alert-warning" style={{ fontSize: '0.8125rem', maxWidth: '68ch', marginTop: '0.5rem' }}>
                    Ordföranden för protokollet och ingen justerare är angiven. Det är
                    tillåtet bara om ordföranden företräder samtliga aktier i bolaget
                    (enmansbolag) — annars kräver aktiebolagslagen (7 kap. 48 §) att
                    protokollet justeras av minst en utsedd justerare.
                </div>
            )}

            <div className="ye-stamma-checks">
                <label className="ye-check-row" htmlFor="ye-stamma-faststallde">
                    <input
                        id="ye-stamma-faststallde"
                        type="checkbox"
                        checked={faststallde}
                        onChange={(e) => setFaststallde(e.target.checked)}
                    />
                    <span>
                        Stämman fastställde resultat- och balansräkningen
                        <span className="ye-field-help" style={{ display: 'block' }}>
                            Obligatoriskt beslut på ordinarie stämma — flödet kan inte
                            fortsätta utan det.
                        </span>
                    </span>
                </label>
                <label className="ye-check-row" htmlFor="ye-stamma-ansvarsfrihet">
                    <input
                        id="ye-stamma-ansvarsfrihet"
                        type="checkbox"
                        checked={ansvarsfrihet}
                        onChange={(e) => setAnsvarsfrihet(e.target.checked)}
                    />
                    <span>
                        Stämman beviljade styrelsen och eventuell VD ansvarsfrihet
                    </span>
                </label>
            </div>

            <h6 className="ye-section-heading" style={{ marginTop: '1.25rem' }}>
                Resultatdisposition — stämmans beslut
            </h6>
            <div className="ye-field-help" style={{ marginBottom: '0.5rem', maxWidth: '68ch' }}>
                Förifyllt från styrelsens förslag i årsredovisningen — stämman fattar
                det slutliga beslutet och kan ändra beloppen här.
            </div>
            <div className="ye-tax-rows">
                <div className="ye-tax-row">
                    <span>Summa till förfogande</span>
                    <span className="ye-num">{formatSek(till)}</span>
                </div>
            </div>
            <div className="ye-form-grid" style={{ marginTop: '0.5rem' }}>
                <div className="ye-field">
                    <label htmlFor="ye-stamma-utdelning">Utdelning (kr)</label>
                    <input
                        id="ye-stamma-utdelning"
                        type="text"
                        inputMode="decimal"
                        placeholder="0"
                        value={utdelningInput}
                        onChange={(e) => setUtdelningInput(e.target.value)}
                    />
                </div>
                <div className="ye-field">
                    <label>Balanseras i ny räkning (kr)</label>
                    <input
                        type="text"
                        value={balanseras != null ? formatSek(Math.max(0, balanseras)) : '—'}
                        disabled
                    />
                </div>
            </div>
            {!dispositionOk && (
                <div className="alert alert-danger mt-2 mb-0" style={{ fontSize: '0.8125rem', maxWidth: 560 }}>
                    Utdelningen kan inte vara större än summan till förfogande
                    ({formatSek(till)}).
                </div>
            )}
            {utdelningValid && utdelningValue > 0 && (
                <div className="ye-field" style={{ marginTop: '0.75rem', maxWidth: 640 }}>
                    <label htmlFor="ye-stamma-yttrande">
                        Styrelsens yttrande om utdelningen (ABL 18 kap. 4 §)
                    </label>
                    <div className="ye-field-help" style={{ marginBottom: '0.35rem', maxWidth: '68ch' }}>
                        Vid utdelning kräver aktiebolagslagen ett motiverat yttrande från
                        styrelsen om att utdelningen är försvarlig enligt
                        försiktighetsregeln (17 kap. 3 §). Förslaget nedan kan redigeras —
                        yttrandet tas med i stämmoprotokollet.
                    </div>
                    <textarea
                        id="ye-stamma-yttrande"
                        className="form-control"
                        rows={4}
                        value={yttrande}
                        onChange={(e) => setYttrande(e.target.value)}
                        onFocus={() => {
                            if (!yttrande.trim()) {
                                setYttrande(
                                    'Styrelsen bedömer att den föreslagna utdelningen om '
                                    + `${formatSek(utdelningValue)} är försvarlig med hänsyn till de krav `
                                    + 'som verksamhetens art, omfattning och risker ställer på storleken av '
                                    + 'det egna kapitalet samt bolagets konsolideringsbehov, likviditet och '
                                    + 'ställning i övrigt (17 kap. 3 § aktiebolagslagen). Full täckning finns '
                                    + 'för bolagets bundna egna kapital efter utdelningen.',
                                );
                            }
                        }}
                        placeholder="Klicka för att fylla i ett förslag…"
                    />
                </div>
            )}

            {stammaError && (
                <div className="alert alert-danger mt-3 mb-0" style={{ fontSize: '0.875rem' }}>
                    {stammaError}
                </div>
            )}
            <div className="ye-proposal-actions">
                <button
                    className="btn btn-primary d-inline-flex align-items-center gap-2"
                    onClick={handleRecordStamma}
                    disabled={registering || !faststallde || !stammaDate
                        || ordforande.trim() === '' || !dispositionOk
                        || (report?.fb_sections?.revisor?.harRevisor === true
                            && !revisionsberattelseDatum)}
                >
                    <Landmark size={16} />
                    {registering ? 'Registrerar…' : 'Registrera stämman'}
                </button>
                <span className="ye-footer-hint">
                    Ett färdigt protokoll tas fram från uppgifterna.
                </span>
            </div>
        </>
    );

    const renderStammaCard = () => (
        <div className={`ye-sub-card ${!isSigned ? 'ye-sub-card-locked' : ''}`}>
            <div className="ye-sub-card-head">
                <span className="ye-sub-card-icon"><Landmark size={18} /></span>
                <div>
                    <div className="ye-sub-card-title">Årsstämma</div>
                    <div className="ye-sub-card-sub">
                        Det formella möte där ägarna fastställer årsredovisningen och
                        beslutar om resultatet. Registrera besluten här så får du ett
                        färdigt protokoll.
                    </div>
                </div>
                {stammaRecorded ? (
                    <span className="ye-badge ye-badge-success" style={{ marginLeft: 'auto' }}>
                        <CheckCircle2 size={13} />
                        Registrerad
                    </span>
                ) : !isSigned ? (
                    <span className="ye-badge ye-badge-neutral" style={{ marginLeft: 'auto' }}>
                        <Lock size={12} />
                        Väntar på underskrifter
                    </span>
                ) : null}
            </div>

            {stammaWarnings.length > 0 && (
                <div className="ye-well" style={{ borderColor: '#fde68a', background: '#fffbeb', color: '#92400e' }}>
                    <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
                        {stammaWarnings.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                </div>
            )}

            {!isSigned ? (
                <div className="ye-well">
                    Bekräfta underskrifterna ovan först — stämman hålls efter att
                    årsredovisningen är underskriven.
                </div>
            ) : stammaRecorded ? renderStammaSummary() : renderStammaForm()}
        </div>
    );

    const renderBody = () => {
        if (loadError) {
            return (
                <>
                    <div className="alert alert-danger" style={{ fontSize: '0.875rem' }}>
                        {loadError}
                    </div>
                    <button
                        className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-2"
                        onClick={() => fetchReport()}
                        disabled={refreshing}
                    >
                        <RefreshCw size={14} className={refreshing ? 'spin' : ''} />
                        Försök igen
                    </button>
                </>
            );
        }
        if (!report) {
            return (
                <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                    Hämtar uppgifterna…
                </p>
            );
        }
        return (
            <>
                {renderSignaturesCard()}
                {renderStammaCard()}
            </>
        );
    };

    // ---- Layout -----------------------------------------------------------

    return (
        <>
            <style>{`
                .ye-sub-card {
                    border: 1px solid #e5e7eb;
                    border-radius: 10px;
                    padding: 1rem 1.25rem;
                }
                .ye-sub-card + .ye-sub-card { margin-top: 1rem; }
                .ye-sub-card-locked { background: #fafafa; }
                .ye-sub-card-head {
                    display: flex;
                    align-items: flex-start;
                    gap: 0.75rem;
                    flex-wrap: wrap;
                }
                .ye-sub-card-icon { color: var(--primary); flex-shrink: 0; margin-top: 2px; }
                .ye-sub-card-title {
                    font-weight: 600;
                    font-size: 0.9375rem;
                    color: var(--title, #374151);
                }
                .ye-sub-card-sub {
                    font-size: 0.8125rem;
                    color: #6b7280;
                    margin-top: 2px;
                    line-height: 1.5;
                    max-width: 62ch;
                }
                .ye-stamma-checks {
                    display: flex;
                    flex-direction: column;
                    gap: 0.6rem;
                    margin-top: 1rem;
                }
                .ye-check-row {
                    display: flex;
                    align-items: flex-start;
                    gap: 0.6rem;
                    font-size: 0.875rem;
                    color: var(--title, #374151);
                    margin: 0;
                    cursor: pointer;
                }
                .ye-check-row input { margin-top: 3px; }
            `}</style>

            <div className="modern-card-header ye-step-header">
                <div>
                    <h4 className="section-title">Underskrifter &amp; årsstämma</h4>
                    <p className="section-subtitle">
                        Här samlar du in styrelsens underskrifter och håller årsstämman —
                        det formella mötet där ägarna fastställer årsredovisningen. Du
                        får ett färdigt protokoll att utgå ifrån.
                    </p>
                </div>
                <div className="action-buttons">
                    {stammaRecorded ? (
                        <span className="ye-badge ye-badge-success">
                            <CheckCircle2 size={13} />
                            Stämman är hållen
                        </span>
                    ) : isSigned ? (
                        <span className="ye-badge ye-badge-primary">Underskriven</span>
                    ) : (
                        <span className="ye-badge ye-badge-neutral">Inte underskriven ännu</span>
                    )}
                </div>
            </div>

            <div className="modern-card-body">
                {renderBody()}

                {gateError && (
                    <div className="alert alert-danger mt-3 mb-0" style={{ fontSize: '0.875rem' }}>
                        <strong>{gateError.message}</strong>
                    </div>
                )}
            </div>

            <div className="ye-step-footer">
                {completed ? (
                    <>
                        <span className="ye-footer-done">
                            <CheckCircle2 size={18} style={{ color: 'var(--bs-success)' }} />
                            Underskrifterna och stämman är klara
                        </span>
                        <button
                            className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
                            onClick={onUndo}
                            disabled={busy}
                        >
                            <Undo2 size={14} />
                            Ångra markeringen
                        </button>
                    </>
                ) : (
                    <>
                        <span className="ye-footer-hint">
                            {stammaRecorded
                                ? 'Stämman är registrerad — markera steget som klart och gå vidare.'
                                : 'Bekräfta underskrifterna och registrera stämman först.'}
                        </span>
                        <button
                            className="btn btn-primary d-inline-flex align-items-center gap-2"
                            onClick={onComplete}
                            disabled={busy}
                        >
                            {busy ? 'Sparar…' : 'Markera som klart'}
                            <ArrowRight size={16} />
                        </button>
                    </>
                )}
            </div>
        </>
    );
};

export default SignaturesStammaStep;
